import { CheckIcon, TicketIcon, WriteIcon } from "@xola/icons";
import { sumBy } from "lodash";
import React from "react";
import { DigitalWaiver, Timeslot } from "types";
import { SkeletonLine } from "../../Common/Skeleton/TableSkeleton";
import { BasicInfo } from "../Common/BasicInfo";
import { useExperience } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useCollection } from "@/hooks/useCollection";
import { isCheckInAllowed } from "@/models/Experience";
import { getConfirmedGuestCount, guestCountForReservedBookings } from "@/models/Timeslot";
import { isProductHasLinkedWaiverTemplate } from "@/models/Waiver";
import { canViewWaivers } from "@/stores/globalStore";

export const GuestWaiverVoucherDisplay = ({ timeslot }: { timeslot: Timeslot }) => {
    const { data: waiverTemplates, isLoading: isLoadingWaivers } = useCollection<DigitalWaiver>(
        "waiverTemplates",
        {},
        { enabled: canViewWaivers() },
    );

    const { voucherCount = 0, waiverParticipantCount = 0, otaPurchaseCount = 0, guestStatusCounts = [] } = timeslot;

    const totalGuestCount = guestCountForReservedBookings(timeslot);
    const { experience, isLoadingExperience } = useExperience(timeslot.product);

    const seller = useSeller();
    const totalVoucherCount = voucherCount + otaPurchaseCount;
    const demographicCount = getConfirmedGuestCount(timeslot);
    const checkedInCount = sumBy(guestStatusCounts, (gc) => (gc.guestStatus === "arrived" ? gc.count : 0)) ?? 0;
    const isWaiverLinked = experience ? isProductHasLinkedWaiverTemplate(waiverTemplates, experience) : false;

    const checkedInBreakdown = (
        <>
            <var data-var="checkedInCount">{checkedInCount}</var> checked-in guests
        </>
    );

    if (isLoadingExperience || isLoadingWaivers || !experience) {
        return (
            <div className="flex items-center space-x-4 !text-gray">
                <div className="flex items-center">
                    <CheckIcon className="mr-1" />
                    <SkeletonLine className="h-4 w-15" />
                </div>

                <div className="flex items-center">
                    <WriteIcon className="mr-1" />
                    <SkeletonLine className="h-4 w-15" />
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center space-x-2 text-gray-darker xl:w-56 2xl:space-x-4">
            {isCheckInAllowed(seller, experience) && (
                <BasicInfo
                    text={checkedInBreakdown}
                    icon={<CheckIcon />}
                    count={checkedInCount}
                    total={demographicCount}
                    className="ui-dashboard-demographics w-18"
                />
            )}

            {isWaiverLinked && !isLoadingWaivers && canViewWaivers() && (
                <BasicInfo
                    text="Waivers"
                    icon={<WriteIcon />}
                    count={waiverParticipantCount}
                    total={totalGuestCount}
                    className="ui-dashboard-waivers w-18"
                />
            )}

            <BasicInfo
                shouldAutoHide
                text="Vouchers"
                icon={<TicketIcon />}
                count={totalVoucherCount}
                className="ui-dashboard-vouchers w-14"
            />
        </div>
    );
};
